import React, { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import "./footer.css";

interface FooterProps {
  year?: number;
}

const Footer: FC<FooterProps> = ({ year }) => {
  const { t } = useTranslation();
  const displayYear = year ?? new Date().getFullYear();
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const faqItems = [1, 2, 3, 4, 5].map((index) => ({
    question: t(`footer.faq${index}Q`),
    answer: t(`footer.faq${index}A`),
  }));

  const toggleFaq = (index: number) => {
    setOpenFaqIndex((prev) => (prev === index ? null : index));
  };

  return (
    <footer className="site-footer">
      <div className="container footer-grid">
        <section className="footer-col footer-col--brand">
          <div className="footer-brand">
            <div className="footer-brand-logo-wrap">
              <img
                src="/images/FSM_logo.png"
                alt={t("footer.logo")}
                className="footer-brand-logo"
              />
            </div>

            <div className="footer-brand-text">
              <h4>Football Shirt Market</h4>
              <p className="footer-powered">{t("footer.poweredBy")}</p>
            </div>
          </div>

          <div className="footer-contact-block">
            <a
              className="footer-mail"
              href="mailto:info@footballshirtmarket.com"
            >
              info@footballshirtmarket.com
            </a>
          </div>

          <div className="footer-social" aria-label={t("footer.followUs")}>
            <a
              href="https://www.instagram.com/footballshirt.market/"
              aria-label="Instagram"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src="/images/instagram-white-icon.png" alt="" />
            </a>

            <a
              href="https://www.facebook.com/profile.php?id=61577122442447"
              aria-label="Facebook"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="white"
                width="24"
                height="24"
                aria-hidden="true"
              >
                <path d="M22 12c0-5.522-4.477-10-10-10S2 6.478 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987H7.898V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
              </svg>
            </a>

            <a
              href="https://www.linkedin.com/company/football-shirt-market/about/?viewAsMember=true"
              aria-label="LinkedIn"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="white"
                width="24"
                height="24"
                aria-hidden="true"
              >
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
            </a>
          </div>
        </section>

        <section className="footer-col">
          <h4>{t("footer.about")}</h4>
          <ul className="footer-list">
            <li>
              <a href="/about">{t("footer.aboutUs")}</a>
            </li>
            <li>
              <a href="/info">{t("footer.howItWorks")}</a>
            </li>
          </ul>
        </section>

        <section className="footer-col">
          <h4>{t("footer.customerService")}</h4>
          <ul className="footer-list">
            <li>
              <a href="/faq">{t("footer.faq")}</a>
            </li>
            <li>
              <a href="/contact">{t("footer.contactPage")}</a>
            </li>
            <li>
              <a href="/bug">{t("footer.bugReport")}</a>
            </li>
          </ul>

          <h4 className="footer-legal-heading">{t("legal.eyebrow")}</h4>
          <ul className="footer-list">
            <li>
              <a href="/terms">{t("footer.terms")}</a>
            </li>
            <li>
              <a href="/privacy">{t("footer.privacy")}</a>
            </li>
            <li>
              <a href="/cookies">{t("footer.cookies")}</a>
            </li>
            <li>
              <a href="/refund">{t("footer.refund")}</a>
            </li>
          </ul>
        </section>

        <section className="footer-col footer-col--faq">
          <h4>{t("footer.faq")}</h4>

          <div className="footer-faq" role="list">
            {faqItems.map((item, index) => {
              const isOpen = openFaqIndex === index;

              return (
                <div
                  className="footer-faq-item"
                  key={`faq-${index}`}
                  role="listitem"
                >
                  <button
                    type="button"
                    className={`footer-faq-question${isOpen ? " is-open" : ""}`}
                    onClick={() => toggleFaq(index)}
                    aria-expanded={isOpen}
                    aria-controls={`footer-faq-panel-${index}`}
                    id={`footer-faq-button-${index}`}
                  >
                    <span>{item.question}</span>
                    <span className="footer-faq-icon" aria-hidden="true">
                      {isOpen ? "−" : "+"}
                    </span>
                  </button>

                  <div
                    id={`footer-faq-panel-${index}`}
                    className={`footer-faq-answer${isOpen ? " is-open" : ""}`}
                    role="region"
                    aria-labelledby={`footer-faq-button-${index}`}
                  >
                    <p>{item.answer}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      <div className="footer-bottom">
        <div className="container footer-bottom-inner">
          <p>
            © <span>{displayYear}</span> Football Shirt Market —{" "}
            {t("footer.allRightsReserved")}
          </p>

          <div className="footer-meta">
            <a href="/terms">{t("footer.terms")}</a>
            <a href="/privacy">{t("footer.privacy")}</a>
            <a href="/cookies">{t("footer.cookies")}</a>
            <a href="/refund">{t("footer.refund")}</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
